import type { Customer } from '@services/index';
import { initialsFor } from './CustomerAvatar';
import type { CustomerPlan, CustomerRow, CustomerStatus } from './types';

// The service's Customer → the register's CustomerRow.
//
// Most fields here come straight off the customer record as the API returns
// it. What the record does not carry reads as absent, never as a default:
// `plan: null` renders no plan, it does not render "STARTER". That is the
// difference between a register you can act on and one that invents facts
// about accounts. `ticketCount` is the one exception — it comes from a
// second, BFF-side call to ticket-service (see customersStore's
// `withEngagement: true`), not from the customer record itself.

const PLANS = new Set<CustomerPlan>(['starter', 'growth', 'enterprise']);
const STATUSES = new Set<CustomerStatus>([
  'active',
  'inactive',
  'blocked',
  'pending',
  'merged',
]);

/** Plan and seat count live in the org's metadata / customFields, if anywhere:
 *  `Organization` has no plan or seats column. Absent when neither carries them. */
function planAndSeats(org: Customer['organization']) {
  const bag = { ...(org?.metadata ?? {}), ...(org?.customFields ?? {}) } as Record<string, unknown>;
  const rawPlan = String(bag.plan ?? bag.tier ?? org?.size ?? '').toLowerCase();
  const rawSeats = Number(bag.seats ?? bag.seatCount ?? NaN);
  return {
    plan: PLANS.has(rawPlan as CustomerPlan) ? (rawPlan as CustomerPlan) : null,
    seats: Number.isFinite(rawSeats) && rawSeats > 0 ? rawSeats : null,
  };
}

export function toRegisterRow(c: Customer): CustomerRow {
  const status = String(c.status ?? '').toLowerCase();
  return {
    id: c.id,
    name: c.name?.trim() || null,
    email: c.email,
    phone: c.phone ?? null,
    avatarInitials: initialsFor(c.name, c.email),
    organisation: c.organization
      ? {
          id: c.organization.id,
          name: c.organization.name,
          ...planAndSeats(c.organization),
        }
      : null,
    // An unrecognised status is bad data (a US state written into `status` by a
    // bad import, say). It renders as inactive rather than as a bogus chip —
    // customerStatus.ts is the single source of what the UI recognises.
    status: STATUSES.has(status as CustomerStatus) ? (status as CustomerStatus) : 'inactive',
    tags: c.tags ?? [],
    lastSeenAt: c.lastSeenAt ?? null,
    createdAt: c.createdAt,
    ticketCount: c.lifetimeTickets ?? 0,
  };
}
